import { useEffect } from 'react';
import { useGetMyInfoQuery } from 'src/hooks/useGetMyInfoQuery';
import { createOrgPath, homePath } from 'src/routes/constants';

import { useNavigate } from 'components/Nav';

import { useUserQuery } from '../views/SignUpPage/queries/useUserQuery';

import { useAcceptInvite } from './useAcceptInvite';
import { useAutoJoin } from './useAutoJoin';

export interface SignUpRoutingState {
  isLoading: boolean;
  error: any;
  user: any;
  invitedEmail: string | undefined;
  claimedDomain: any;
  isAutoJoinPending: boolean;
  myInfo: any;
}

/**
 * Custom hook that composes signup routing flows
 * Each flow is handled by dedicated hooks: useAcceptInvite, useAutoJoin
 */
export const useSignUpRouting = (): SignUpRoutingState => {
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useUserQuery();
  const { data: myInfo, isLoading: isLoadingMyInfo } = useGetMyInfoQuery();
  
  const orgId = myInfo?.orgRole?.org.id;

    // Individual flows handled by dedicated hooks
    const { isAcceptingInvite, invitedEmail } = useAcceptInvite();
    const { isAutoJoinLoading, claimedDomain, isAutoJoinPending } = useAutoJoin();
  
    // Handle navigation to home if user already has org
    useEffect(() => {
      if (!isLoadingMyInfo && !isLoading && orgId) {
        console.debug('User has orgId, navigating to /home/projects');
        navigate(homePath);
      }
    }, [isLoadingMyInfo, isLoading, orgId, navigate]);
  
    // Handle navigation to create org if no other flows are active
  useEffect(() => {
    if (!isLoadingMyInfo && 
      !isLoading && 
      user && 
      myInfo && 
      !orgId && 
      !invitedEmail && 
      !isAutoJoinLoading && 
      !isAutoJoinPending) {
    console.debug('No invite or auto-join, navigating to create org');
    navigate(createOrgPath);
    }
  }, [
    isLoadingMyInfo,
    isLoading,
    user,
    myInfo,
    orgId,
    invitedEmail,
    isAutoJoinLoading,
    isAutoJoinPending,
    navigate,
  ]);

  return {
    isLoading: isLoadingMyInfo || isLoading || isAcceptingInvite || isAutoJoinLoading || isAutoJoinPending,
    error,
    user,
    invitedEmail,
    claimedDomain,
    isAutoJoinPending,
    myInfo,
  };
};